import axios from 'axios';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import each from 'lodash/each';
import findIndex from 'lodash/findIndex';

import config from 'config';
import {PaginatableCollection} from 'domains/base/collections';
import {Product} from './models';

export default {
    state: {
        products: new PaginatableCollection(),
        currentProduct: false,
        configs: {},
        eligiblePrintersListForCurrentProduct: null,
    },
    actions: {
        async getProducts({rootState, state, commit}) {
            let response = await axios.get(`${config.API_DOMAIN}${rootState.user._links.products.search}?page=${rootState.route.params.page ? rootState.route.params.page - 1 : 0}`);

            let products = clone(response.data);

            each(products._embedded.items, product => {
                product.loading = false;
                product.editing = false;
            });

            commit('setPaginatableCollection', {paginatableCollection: state.products, data: products});

            return response;
        },
        async getProduct({rootState, state, commit}) {
            let response = await axios.get(`${config.API_DOMAIN}${rootState.user._links.products.get.replace('{productId}', rootState.route.params.id)}`);

            commit('updateState', {state, property: 'currentProduct', value: new Product(response.data)});

            return response;
        },
        async createProduct({rootState, state, dispatch, commit}) {
            let imageUploadPromises = [];

            if (state.currentProduct.frontImage) {
                imageUploadPromises.push(dispatch('uploadProductImage', {
                    file: state.currentProduct.frontImage,
                    property: 'front_image'
                }));
            }

            if (state.currentProduct.frontMask) {
                imageUploadPromises.push(dispatch('uploadProductImage', {
                    file: state.currentProduct.frontMask,
                    property: 'front_mask'
                }));
            }

            if (state.currentProduct.backImage) {
                imageUploadPromises.push(dispatch('uploadProductImage', {
                    file: state.currentProduct.backImage,
                    property: 'back_image'
                }));
            }

            if (state.currentProduct.backMask) {
                imageUploadPromises.push(dispatch('uploadProductImage', {
                    file: state.currentProduct.backMask,
                    property: 'back_mask'
                }));
            }

            await Promise.all(imageUploadPromises);

            let payload = cloneDeep(state.currentProduct);

            delete payload._links;
            delete payload.id;
            delete payload.backMaskUri;
            delete payload.frontMaskUri;
            delete payload.frontImageUri;
            delete payload.backImageUri;

            each(payload.sizes, size => {
                delete size.key;

                // TODO remove this
                size.active = true;
            });

            each(payload.colors, color => {
                let overrides = [];

                each(color.product_color_size_overrides, override => {
                    if (override.printer_id !== null) {
                        overrides.push(override);
                    }
                });

                color.product_color_size_overrides = overrides;
            });

            let response = await axios.post(`${config.API_DOMAIN}${rootState.user._links.products.create}`, payload);

            commit('updateState', {state: state.currentProduct, property: 'frontImage', value: null});
            commit('updateState', {state: state.currentProduct, property: 'frontMask', value: null});
            commit('updateState', {state: state.currentProduct, property: 'backImage', value: null});
            commit('updateState', {state: state.currentProduct, property: 'backMask', value: null});

            commit('updateState', {state: state.currentProduct, property: 'frontImageUri', value: null});
            commit('updateState', {state: state.currentProduct, property: 'frontMaskUri', value: null});
            commit('updateState', {state: state.currentProduct, property: 'backImageUri', value: null});
            commit('updateState', {state: state.currentProduct, property: 'backMaskUri', value: null});

            return response;
        },
        async updateProduct({state, dispatch, commit}) {
            let imageUploadPromises = [];

            if (state.currentProduct.frontImage) {
                imageUploadPromises.push(dispatch('uploadProductImage', {
                    file: state.currentProduct.frontImage,
                    property: 'front_image'
                }));
            }

            if (state.currentProduct.frontMask) {
                imageUploadPromises.push(dispatch('uploadProductImage', {
                    file: state.currentProduct.frontMask,
                    property: 'front_mask'
                }));
            }

            if (state.currentProduct.backImage) {
                imageUploadPromises.push(dispatch('uploadProductImage', {
                    file: state.currentProduct.backImage,
                    property: 'back_image'
                }));
            }

            if (state.currentProduct.backMask) {
                imageUploadPromises.push(dispatch('uploadProductImage', {
                    file: state.currentProduct.backMask,
                    property: 'back_mask'
                }));
            }

            await Promise.all(imageUploadPromises);

            let payload = cloneDeep(state.currentProduct);

            delete payload._links;
            delete payload.id;
            delete payload.backMaskUri;
            delete payload.frontMaskUri;
            delete payload.frontImageUri;
            delete payload.backImageUri;

            each(payload.sizes, size => {
                delete size.key;

                // TODO remove this
                size.active = true;
            });

            each(payload.colors, color => {
                let overrides = [];

                each(color.product_color_size_overrides, override => {
                    if (override.printer_id !== null) {
                        overrides.push(override);
                    }
                });

                color.product_color_size_overrides = overrides;
            });

            let response = await axios.put(state.currentProduct._links.update, payload);

            commit('updateState', {state: state.currentProduct, property: 'frontImage', value: null});
            commit('updateState', {state: state.currentProduct, property: 'frontMask', value: null});
            commit('updateState', {state: state.currentProduct, property: 'backImage', value: null});
            commit('updateState', {state: state.currentProduct, property: 'backMask', value: null});

            commit('updateState', {state: state.currentProduct, property: 'frontImageUri', value: null});
            commit('updateState', {state: state.currentProduct, property: 'frontMaskUri', value: null});
            commit('updateState', {state: state.currentProduct, property: 'backImageUri', value: null});
            commit('updateState', {state: state.currentProduct, property: 'backMaskUri', value: null});

            return response;
        },
        async activateProduct({dispatch}, product) {
            let response = await axios.patch(product._links.activate);

            dispatch('getProducts');

            return response;
        },
        async deactivateProduct({dispatch}, product) {
            let response = await axios.patch(product._links.deactivate);

            dispatch('getProducts');

            return response;
        },
        async getProductConfigs({rootState, state, commit}) {
            let response = await axios.get(`${config.API_DOMAIN}${rootState.user._links.products.configs}`);

            commit('updateState', {state, property: 'configs', value: response.data});

            return response;
        },
        async uploadProductImage({rootState, state, commit}, {file, property}) {
            let form = new FormData();

            form.append('image', file);

            let response = await axios.post(`${config.API_DOMAIN}${rootState.user._links.products.upload}`, form);

            commit('updateState', {state: state.currentProduct, property, value: response.data.product_image});

            return response;
        },
        async getEligiblePrintersListForCurrentProduct({state, commit}) {
            let response = await axios.get(state.currentProduct._links['eligible-printers']);

            commit('updateState', {
                state,
                property: 'eligiblePrintersListForCurrentProduct',
                value: response.data.printers
            });

            return response;
        },
    },
    mutations: {
        setCurrentProductSizes(state, sizes) {
            state.currentProduct.sizes = sizes;

            each(sizes, size => {
                each(state.currentProduct.colors, color => {
                    color.product_color_size_overrides = [{
                        size: size.size,
                        printer_id: null,
                    }];
                });
            });
        },
        addCurrentProductSizes(state, size) {
            state.currentProduct.sizes = [...state.currentProduct.sizes, size];

            each(state.currentProduct.colors, color => {
                color.product_color_size_overrides.push({
                    size: size.size,
                    printer_id: null,
                });
            });
        },
        removeCurrentProductSize(state, size) {
            state.currentProduct.sizes.splice(findIndex(state.currentProduct.sizes, currentSize => currentSize.size === size), 1);

            each(state.currentProduct.colors, color => {
                color.product_color_size_overrides.splice(findIndex(color.product_color_size_overrides, override => override.size === size), 1);
            });
        },
    },
};
